import { useEffect, useRef } from 'react'

import { LogUserActivity } from '@/apis/users/requests.ts'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import StudentStatusBadge from '@/app/common/components/StudentStatusBadge'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import {
  LOG_TYPES,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_FORMATTED,
} from '@/common/constants.ts'
import { UserCheckIcon } from '@/common/icons/general.tsx'
import { cn, currencyFormatter } from '@/lib/utils'
import { useAssignTask } from '@/pages/tasks/hooks/useAssignTask.ts'
import { useUnassignTask } from '@/pages/tasks/hooks/useGetUnassingnTask.ts'
import { StateList } from '@/queries/common/requests'
import {
  AwardDetail,
  FamilyApplicationDetails,
  SHOULD_DISPLAY_PURSE_STATUS,
} from '@/queries/family_applications/requests.ts'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { setTask } from '@/storage/tasks/slice.ts'
import { Landmark, MessageCircle, PhoneIcon, User } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import DefaultAvatar from '../../profile/components/DefaultAvatar'
import InfoField from './components/InfoField'

type ApplicationDetailsViewProps = {
  ApplicantData: FamilyApplicationDetails
  awardDetails: AwardDetail[]
  states: StateList
}

/**
 * Capitalizes the first letter of each word and removes underscores
 */
function capitalizeAndRemoveUnderscores(text: string | null): string {
  if (!text) {
    return ''
  }
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function ApplicationDetailsView({
  ApplicantData,
  states,
  awardDetails,
}: ApplicationDetailsViewProps) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const task_id = params.get('task_id') ?? ''

  const dispatch = useDispatch()

  const { mutate: unassignTask } = useUnassignTask()
  const { mutate: assignTask } = useAssignTask()

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMounted = useRef(true)

  const hasLoggedPageView = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      unassignTask()
    }
  }, [])

  useEffect(() => {
    assignTask(parseInt(task_id) ?? 0, {
      onSuccess: (data) => {
        dispatch(setTask(data))
      },
    })
  }, [task_id])

  useEffect(() => {
    if (!hasLoggedPageView.current && ApplicantData) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Application details',
        targetUserName: ApplicantData.name + ' (APPLICANT)',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  // This is a workaround to get all students from the backend.
  // There really isn't a good reason as to why we're doing this, but it's what we have to work with.
  const allStudents = [
    ...(ApplicantData.students ? [ApplicantData.students] : []),
    ...(ApplicantData.other_students || []),
  ]

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
  function getAddress() {
    const state = states.data.find(
      (state) => state.id === ApplicantData.global_state_id
    )
    if (!ApplicantData.address || !state?.name) {
      return null
    }

    return `${ApplicantData.address}, ${ApplicantData.city}, ${state.name}, ${ApplicantData.zip_code} `
  }

  const scrollMarginClass = 'scroll-mt-24'
  return (
    <div className="max-w-screen-2xl w-full mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center justify-between ">
          <div className="text-2xl font-medium ">Applicant Information</div>
        </div>
        <Dialog>
          <DialogTrigger
            className={buttonVariants({
              variant: 'outline',
            })}
          >
            Report Incorrect Information
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Update Your Information</DialogTitle>
              <DialogDescription>
                This information is managed by ACE Scholarships. If you need to
                update any details, please contact us and we'll assist you.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-4 py-4">
              <div className="flex-1 min-w-0 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <PhoneIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-600">Phone</div>
                  <a
                    href={`tel:${SUPPORT_PHONE}`}
                    className="text-base text-gray-900 truncate"
                  >
                    {SUPPORT_PHONE_FORMATTED}
                  </a>
                </div>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <MessageCircle className="size-5 text-gray-600 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-600">Email</div>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-base text-gray-900 hover:underline truncate block"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose className="w-full" asChild>
                <button
                  className={buttonVariants({
                    className: 'w-full',
                    variant: 'outline',
                  })}
                >
                  Close
                </button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-12 gap-8">
        <aside className="col-span-3">
          <Card className="col-span-3 h-fit sticky top-20">
            <CardContent>
              <div className="mb-6">
                <div className="text-gray-800 font-medium mb-2">
                  Applicant Details
                </div>
                <button
                  onClick={() => scrollToSection('applicant-details')}
                  className="flex items-center gap-x-2 py-2 hover:bg-gray-100 rounded-md px-2 w-full text-sm text-gray-800"
                >
                  <UserCheckIcon />
                  {ApplicantData.first_name} {ApplicantData.last_name}
                </button>
                <button
                  onClick={() => scrollToSection('income-data')}
                  className="flex items-center gap-x-2 py-2 hover:bg-gray-100 rounded-md px-2 w-full text-sm text-gray-800"
                >
                  <Landmark className="w-4 h-4" /> Income Data
                </button>
              </div>
              <div>
                <div className="font-medium text-gray-800 mb-3">Students</div>
                <div className="space-y-1">
                  {allStudents.map((student) => {
                    const studentAwards = awardDetails.filter(
                      (award) => award.student_id === student.id
                    )
                    const hasPurse = studentAwards.length > 0
                    const totalBalance = studentAwards.reduce(
                      (sum, award) => sum + (award.balance_available ?? 0),
                      0
                    )

                    return (
                      <button
                        key={student.id}
                        onClick={() => scrollToSection(`student-${student.id}`)}
                        className="flex flex-col text-left w-full hover:bg-gray-100 rounded-md px-2 py-2 text-sm text-gray-800"
                      >
                        <div className="flex items-center gap-x-2 ">
                          <User className="w-4 h-4 text-gray-600" />
                          <div className="font-medium text-gray-800">
                            {student.name}
                          </div>
                        </div>
                        {hasPurse ? (
                          <div className="text-xs text-gray-600 ml-6">
                            Purse: {currencyFormatter.format(totalBalance)}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 ml-6">
                            No purse
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>
        <main className="col-span-9 flex flex-col items-center max-w-screen-xl mx-auto w-full gap-8">
          <Card className=" w-full">
            <CardContent className="flex items-center justify-between">
              <div className="flex items-center gap-x-2">
                <DefaultAvatar
                  firstName={ApplicantData.first_name}
                  lastName={ApplicantData.last_name}
                />
                <div>
                  <div className="text-gray-800 text-lg font-medium">
                    {ApplicantData.first_name} {ApplicantData.last_name}
                  </div>
                  <div className="text-gray-600 text-sm font-normal">
                    {ApplicantData.email}
                  </div>
                </div>
              </div>
              <div>
                <InfoField label="Applicant ID" value={ApplicantData.user_id} />
              </div>
            </CardContent>
          </Card>
          <Card
            id="applicant-details"
            className={cn('w-full', scrollMarginClass)}
          >
            <CardHeader className="text-gray-800 font-medium text-xl">
              {ApplicantData.first_name} {ApplicantData.last_name} - Applicant
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                <InfoField
                  label="Name"
                  value={`${ApplicantData.first_name} ${ApplicantData.last_name}`}
                />
                <InfoField label="Address" value={getAddress()} />
                <InfoField
                  label="Date Of Birth"
                  value={ApplicantData.date_of_birth}
                />
                <InfoField
                  label="Military Exemption Applicable?"
                  value={ApplicantData.is_military_exemption ? 'Yes' : 'No'}
                />
              </div>
            </CardContent>
          </Card>
          <Card id="income-data" className={cn(scrollMarginClass, 'w-full')}>
            <CardHeader className="text-gray-800 font-medium text-xl">
              Income Data
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                <InfoField
                  label="Household Size"
                  value={ApplicantData.household_inhabitants}
                />
                <InfoField
                  label="Filing Type"
                  value={capitalizeAndRemoveUnderscores(
                    ApplicantData.household_filed_federal_income
                  )}
                />

                <InfoField
                  label="Gross Income"
                  value={
                    Number.isFinite(ApplicantData.income)
                      ? currencyFormatter.format(ApplicantData.income!)
                      : null
                  }
                />

                <InfoField
                  label="Verified Tax Year"
                  value={ApplicantData.verified_income_tax_year}
                />

                <InfoField
                  label="Spouse Income"
                  value={
                    Number.isFinite(ApplicantData.spouse_income)
                      ? currencyFormatter.format(ApplicantData.spouse_income!)
                      : null
                  }
                />

                <InfoField
                  label="Spouse Verified Tax Year"
                  value={ApplicantData.spouse_verified_tax_year}
                />
              </div>
            </CardContent>
          </Card>
          {allStudents.map((student) => {
            const studentAwards = awardDetails.filter((award) => {
              return award.student_id === student.id
            })

            return (
              <div
                key={student.id}
                id={`student-${student.id}`}
                className={cn(scrollMarginClass, 'w-full space-y-6')}
              >
                <Card className="w-full">
                  <CardHeader className="flex items-center justify-between">
                    <div className="text-gray-800 font-medium text-xl">
                      {student.name} - ID: {student.id}
                    </div>
                    <StudentStatusBadge
                      status={student.scholarship_applications_status}
                      subStatus={student.scholarship_applications_sub_status}
                    />
                  </CardHeader>
                  <CardContent className="space-y-8">
                    <InfoField label="Name" value={student.name} />
                    <InfoField
                      label="Sequence Number"
                      value={student.sequence_number}
                    />
                    <InfoField
                      label="Date of Birth"
                      value={student.date_of_birth}
                    />
                    <InfoField label="Grade" value={student.grade} />
                    <InfoField
                      label="Relationship"
                      value={
                        student.relationship
                          ? capitalizeAndRemoveUnderscores(student.relationship)
                          : null
                      }
                    />
                    {SHOULD_DISPLAY_PURSE_STATUS.includes(
                      student.scholarship_applications_status
                    ) && (
                      <InfoField
                        label="Student Eligible Purse ID(s)"
                        value={
                          student.student_program_purses &&
                          student.student_program_purses
                            .map((purse) => purse.id)
                            .join(', ')
                        }
                      />
                    )}
                    {/* Award Details Section */}
                    {studentAwards.length > 0 && (
                      <div className="pt-6 border-t border-gray-200">
                        <div className="mb-4">
                          <div className="text-gray-800 font-medium text-lg">
                            Award Details ({studentAwards.length})
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {studentAwards.map((award, index) => {
                            const safeCurrency = (
                              value: number | null | undefined
                            ) =>
                              value != null
                                ? currencyFormatter.format(value)
                                : 'N/A'

                            return (
                              <Card
                                key={`${award.student_id}-${index}`}
                                id={`award-${award.student_id}-${index}`}
                                className={cn(
                                  'w-full shadow-none',
                                  scrollMarginClass
                                )}
                              >
                                <CardContent className="space-y-3 pt-4 pb-4">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">
                                      Awarded Amount
                                    </span>
                                    <span className="text-xs font-medium text-gray-900">
                                      {safeCurrency(award.awarded_amount)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">
                                      Award Year
                                    </span>
                                    <span className="text-xs font-medium text-gray-900">
                                      {award.awarded_year_initial ?? 'N/A'}
                                    </span>
                                  </div>
                                  <div className="pt-3 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-gray-600">
                                        Available Balance
                                      </span>
                                      <span className="text-xs font-medium text-gray-900">
                                        {safeCurrency(award.balance_available)}
                                      </span>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )
          })}
        </main>
      </div>
    </div>
  )
}
